import {
  getNavigationControl,
  getMarker,
  type GeoJSONSourceType,
} from './mapbox-global'
import type { Map } from 'mapbox-gl'

type MapboxPosition =
  | 'top-right'
  | 'top-left'
  | 'bottom-right'
  | 'bottom-left'
  | undefined

export function createMapboxControls(
  mapbox: Map,
  position: MapboxPosition = 'top-right',
): void {
  const NavigationControl = getNavigationControl()
  mapbox.addControl(
    new NavigationControl({
      visualizePitch: false,
      showCompass: false,
    }),
    position,
  )
}

export function createMapboxMarker(
  mapbox: Map,
  { longitude, latitude }: { longitude: number; latitude: number },
  { label, index }: { label: string | null; index: number },
): void {
  const marker = label
    ? createLabelledMarkerElement(label, index)
    : createMarkerElement()

  const Marker = getMarker()
  new Marker(marker).setLngLat([longitude, latitude]).addTo(mapbox)
}

export function createMarkerElement() {
  const marker = document.createElement('svg')
  marker.className = 'marker'

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
  svg.setAttribute('width', '16')
  svg.setAttribute('height', '16')

  svg.innerHTML = `
        <rect x="1" y="1" width="14" height="14" stroke="#F5B800" stroke-width="2"  fill="transparent"/>
        <rect x="4" y="4" width="8" height="8" fill="#FFC719" />
      `

  marker.appendChild(svg)
  return marker
}

export function createMarkerLabelElement(labelText: string) {
  const label = document.createElement('div')
  label.className = 'map-marker__label'

  const labelInner = document.createElement('div')
  labelInner.className = 'map-marker__label-inner'

  const labelSpan = document.createElement('span')
  labelSpan.className = 'type-subtitle map-marker__label-text'
  labelSpan.innerText = labelText

  labelInner.appendChild(labelSpan)
  label.appendChild(labelInner)
  return label
}

export function createLabelledMarkerElement(labelText: string, index: number) {
  const labelledMarker = document.createElement('div')
  labelledMarker.className = 'map-marker'
  labelledMarker.dataset.destinationIndex = index.toString()
  labelledMarker.setAttribute(
    'x-on:click',
    `handleDestinationPointClick(${index})`,
  )

  const labelledMarkerInner = document.createElement('div')
  labelledMarkerInner.className = 'map-marker__inner'

  const marker = createMarkerElement()
  const label = createMarkerLabelElement(labelText)

  labelledMarkerInner.appendChild(label)
  labelledMarkerInner.appendChild(marker)

  labelledMarker.appendChild(labelledMarkerInner)
  return labelledMarker
}

export function addRouteLayer(mapbox: Map, routeId: number = 0): void {
  const routeLayerExists = mapbox.getLayer(`route-layer-${routeId}`)

  if (routeLayerExists) {
    return
  }

  mapbox.addLayer({
    id: `route-layer-${routeId}`,
    type: 'line',
    source: `route-source-${routeId}`,
    layout: {
      'line-join': 'round',
      'line-cap': 'round',
    },
    paint: {
      'line-color': '#FFC719',
      'line-width': 2,
      'line-dasharray': [4, 3],
    },
  })
}

export function addRouteSource(
  mapbox: Map,
  routes: [number, number][],
  routeId: number = 0,
): void {
  const routeSourceExists = mapbox.getSource(`route-source-${routeId}`)

  if (routeSourceExists) {
    return
  }

  mapbox.addSource(`route-source-${routeId}`, {
    type: 'geojson',
    data: {
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'LineString',
        coordinates: routes,
      },
    },
  })
}

export function updateRouteSource(
  mapbox: Map,
  routes: [number, number][],
  routeId: number = 0,
): void {
  ;(mapbox.getSource(`route-source-${routeId}`) as GeoJSONSourceType).setData({
    type: 'Feature',
    properties: {},
    geometry: {
      type: 'LineString',
      coordinates: routes,
    },
  })
}
